import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotificationCenter } from "../../context/NotificationCenterContext";

const TYPE_ICONS = {
  ticket_assigned_to_me: "👤",
  ticket_reopened: "↩️",
  ticket_first_customer_reply: "📨",
  ticket_customer_replied: "✉️",
  ticket_internal_note_sent_to_me: "📝",
  ticket_mentioned: "@",
  ticket_closed: "✅",
  ticket_escalated: "⚡",
  agent_run_awaiting_review: "🤖",
  agent_run_approved: "✅",
  agent_run_failed: "⚠️",
};

function timeAgo(ts) {
  if (!ts) return "";
  const utc = !ts.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(ts) ? ts + "Z" : ts;
  const diff = Date.now() - new Date(utc).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "עכשיו";
  if (m < 60) return `לפני ${m} דקות`;
  const h = Math.floor(m / 60);
  if (h < 24) return `לפני ${h} שעות`;
  const d = Math.floor(h / 24);
  if (d < 30) return `לפני ${d} ימים`;
  return new Date(utc).toLocaleDateString("he-IL");
}

export default function NotificationBell() {
  const { items, unreadCount, markRead, markAllRead } = useNotificationCenter();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onItemClick = (n) => {
    if (!n.is_read) markRead(n.id);
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
  };

  return (
    <div ref={rootRef} className="relative" dir="rtl">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
        aria-label="התראות"
        title="התראות"
      >
        <span className="text-xl leading-none">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute top-1 left-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed z-50 top-16 left-1/2 -translate-x-1/2 w-[92vw] max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          dir="rtl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">התראות</h3>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unreadCount === 0}
              className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            >
              סמן הכל כנקרא
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-10">אין התראות להצגה</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  className={`w-full text-right flex items-start gap-3 px-4 py-3 border-b last:border-0 transition-colors ${
                    n.is_read ? "bg-white hover:bg-gray-50" : "bg-blue-50/60 hover:bg-blue-50"
                  }`}
                >
                  <span className="shrink-0 text-lg leading-none mt-0.5">{TYPE_ICONS[n.type] ?? "🔔"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {!n.is_read && <span className="shrink-0 w-2 h-2 rounded-full bg-blue-600" />}
                      <span className="font-medium text-sm text-gray-800 truncate">{n.title}</span>
                    </div>
                    {n.message && <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>}
                    <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
